import { ApolloProvider } from '@apollo/client/react';
import type { ReactNode } from 'react';
import { Helmet } from 'react-helmet-async';
import { Outlet, matchPath, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Container } from '@sorare/core/src/atoms/layout/Container';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandL } from '@sorare/core/src/atoms/typography';
import { BLOG, P_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import { NoIndex } from '@sorare/core/src/contexts/seo';
import { useBasenameLocale } from '@sorare/core/src/hooks/useBasenameLocale';

import { client } from 'lib/graphql';

type LayoutProps = {
  children?: ReactNode;
};

const BlogContainer = styled(Vertical).attrs({
  gap: 1,
})`
  padding: var(--triple-unit) 0;
  color: var(--c-white);

  & hr {
    margin: 2em 0 0 0;
  }
`;

const DraftRibbon = styled.div`
  position: fixed;
  top: 20px;
  left: -20px;
  white-space: nowrap;
  transform: rotate(-35deg);
  z-index: 2;
  background-color: var(--c-red-400);
  color: var(--c-white);
  font-weight: bold;
  text-align: center;
  padding: 3px 50px;
`;

const StyledBlogButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  align-self: flex-start;
`;

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { pathname } = useLocation();
  const { basename } = useBasenameLocale();
  const preview = matchPath(P_SLUG, pathname);
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <meta name="twitter:widgets:theme" content="dark" />
        <meta name="twitter:dnt" content="on" />
        <link
          href="https://soraremedium.wpcomstaging.com/wp-content/plugins/gutenberg/build/block-library/style.css"
          rel="stylesheet"
        />
      </Helmet>
      {!!basename && <NoIndex />}
      {!!preview && <DraftRibbon>DRAFT</DraftRibbon>}
      <Container>
        <BlogContainer>
          <ApolloProvider client={client}>
            <StyledBlogButton onClick={() => navigate(BLOG)}>
              <BrandL as="h1">Blog</BrandL>
            </StyledBlogButton>
            {children || <Outlet />}
          </ApolloProvider>
        </BlogContainer>
      </Container>
    </>
  );
};
